import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowUpRight, BarChart3, Clock3, Download,
  Eye, FileBarChart, Mail, MousePointer, Radar, Send, ShieldCheck,
  Target, TrendingUp, Users,
} from 'lucide-react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from 'recharts';
import { Card } from '../components/ui/Card';
import { analyticsApi } from '../api/analytics';
import type { RiskScoreBreakdown, TrendData } from '../api/analytics';
import { campaignsApi } from '../api/campaigns';
import type { CampaignSummaryRow } from '../api/campaigns';
import type { AnalyticsOverview, DepartmentStats } from '../types';
import './operations.css';

type ExportFormat = 'csv' | 'pdf' | 'xlsx';
type TrendPeriod = '7d' | '30d' | '90d';

const percent = (value: number | undefined) =>
  value == null || !Number.isFinite(value) ? 'Unavailable' : `${value.toFixed(1)}%`;

export function Dashboard() {
  const [period, setPeriod] = useState<TrendPeriod>('30d');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data: overview, isLoading: overviewLoading, isError: overviewError } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: async () => (await analyticsApi.getOverview()).data,
  });
  const { data: campaigns, isLoading: campaignsLoading, isError: campaignsError } = useQuery({
    queryKey: ['campaigns-summary'],
    queryFn: async () => (await campaignsApi.getSummary()).data.campaigns,
  });
  const { data: trends, isLoading: trendsLoading, isError: trendsError } = useQuery({
    queryKey: ['analytics-trends', period],
    queryFn: async () => (await analyticsApi.getTrends(period)).data,
  });
  const { data: departments, isLoading: departmentsLoading, isError: departmentsError } = useQuery({
    queryKey: ['analytics-departments'],
    queryFn: async () => (await analyticsApi.getDepartments()).data,
  });
  const { data: riskBreakdown, isLoading: riskLoading, isError: riskError } = useQuery({
    queryKey: ['analytics-risk-score'],
    queryFn: async () => (await analyticsApi.getRiskScore()).data,
  });

  const risk = !overviewError && (overview?.emails_sent ?? 0) > 0 && Number.isFinite(overview?.risk_score)
    ? overview!.risk_score
    : null;
  const orderedTrends = useMemo(
    () => [...(trends ?? [])].sort((left, right) => left.date.localeCompare(right.date)),
    [trends],
  );
  const rankedDepartments = useMemo(() => rankDepartments(departments ?? []), [departments]);

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const response = await analyticsApi.export(exportFormat);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-export.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(`Could not export analytics as ${exportFormat.toUpperCase()}. Please try again later.`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="operations-page dashboard-page space-y-6">
      <header className="dashboard-heading">
        <div>
          <p className="operations-eyebrow">Overview / Security awareness</p>
          <h1>Human risk command center</h1>
          <p className="operations-description">Track simulation outcomes, identify exposure, and prioritize the next awareness action.</p>
        </div>
        <div className="dashboard-heading-actions">
          <div className="dashboard-period" aria-label="Analytics period">
            {(['7d', '30d', '90d'] as TrendPeriod[]).map(value => (
              <button
                key={value}
                type="button"
                className={period === value ? 'is-active' : ''}
                aria-pressed={period === value}
                onClick={() => setPeriod(value)}
              >
                {value.replace('d', ' days')}
              </button>
            ))}
          </div>
          <Link to="/campaigns/new" className="btn-primary dashboard-launch"><Send size={16} /> Launch simulation</Link>
        </div>
      </header>

      {overviewError && <p role="alert" className="dashboard-alert">Could not load dashboard analytics. Other available panels are shown below.</p>}

      <section className="dashboard-command-grid" aria-label="Risk command panel">
        <Card className="dashboard-risk-card">
          <div className="dashboard-risk-copy">
            <span className="dashboard-section-kicker"><ShieldCheck size={15} /> Observed behavior index</span>
            <h2>{overviewLoading || riskLoading ? 'Loading risk posture…' : risk === null ? 'Risk data unavailable' : `${riskBreakdown?.level ?? getRiskBand(risk)} observed risk`}</h2>
            <p>{risk === null ? 'Run a campaign to establish a behavior baseline.' : getRiskLabel(risk)}</p>
            <p className="dashboard-risk-disclaimer">
              Based on {overview?.emails_sent ?? 0} delivered simulation emails. This describes campaign observations, not employee intent or future behavior.
            </p>
          </div>
          <RiskGauge score={risk} loading={overviewLoading || riskLoading} />
          <div className="dashboard-risk-actions">
            <p className="dashboard-section-kicker"><Target size={15} /> Recommended focus</p>
            {riskError ? (
              <p className="dashboard-muted">Recommendations are temporarily unavailable.</p>
            ) : (
              <ul>
                {(riskBreakdown?.recommendations ?? defaultRecommendations(overview)).slice(0, 3).map(recommendation => (
                  <li key={recommendation}>{recommendation}</li>
                ))}
              </ul>
            )}
            <Link to="/analytics/reports">Open analytics reports <ArrowUpRight size={15} /></Link>
          </div>
        </Card>
      </section>

      <section aria-label="Campaign performance metrics" className="dashboard-kpis">
        <DashboardMetric title="Delivered" value={metricValue(overviewLoading, overviewError, overview?.emails_sent)} detail={`${overview?.total_campaigns ?? 0} campaigns`} icon={<Mail />} tone="neutral" />
        <DashboardMetric title="Opened" value={derivedCount(overview, 'open_rate', overviewLoading, overviewError)} detail={percent(overviewError ? undefined : overview?.open_rate)} icon={<Eye />} tone="info" />
        <DashboardMetric title="Clicked" value={derivedCount(overview, 'click_rate', overviewLoading, overviewError)} detail={percent(overviewError ? undefined : overview?.click_rate)} icon={<MousePointer />} tone="warning" />
        <DashboardMetric title="Submitted" value={derivedCount(overview, 'submit_rate', overviewLoading, overviewError)} detail={percent(overviewError ? undefined : overview?.submit_rate)} icon={<AlertTriangle />} tone="danger" />
        <DashboardMetric title="Reported" value={derivedCount(overview, 'report_rate', overviewLoading, overviewError)} detail={percent(overviewError ? undefined : overview?.report_rate)} icon={<ShieldCheck />} tone="success" />
        <DashboardMetric title="Avg. time to click" value={metricValue(overviewLoading, overviewError, overview?.avg_time_to_click)} detail="Across clicked simulations" icon={<Clock3 />} tone="accent" />
      </section>

      <div className="dashboard-analytics-grid">
        <Card className="dashboard-trend-card">
          <SectionHeading eyebrow="Behavior trend" title={`Rates over ${period.replace('d', ' days')}`} icon={<TrendingUp />} />
          <TrendPanel data={orderedTrends} loading={trendsLoading} error={trendsError} />
        </Card>
        <Card className="dashboard-funnel-card">
          <SectionHeading eyebrow="Conversion path" title="Simulation funnel" icon={<Activity />} />
          <FunnelPanel overview={overviewError ? undefined : overview} loading={overviewLoading} />
        </Card>
      </div>

      <div className="dashboard-intelligence-grid">
        <Card>
          <SectionHeading eyebrow="Audience intelligence" title="Departments needing focus" icon={<BarChart3 />} />
          <DepartmentPanel departments={rankedDepartments} loading={departmentsLoading} error={departmentsError} />
        </Card>
        <Card className="dashboard-priority-card">
          <SectionHeading eyebrow="Next best actions" title="Program priorities" icon={<Target />} />
          <PriorityActions overview={overviewError ? undefined : overview} risk={riskBreakdown} />
        </Card>
      </div>

      <Card className="dashboard-campaign-card">
        <div className="dashboard-card-heading">
          <SectionHeading eyebrow="Simulation activity" title="Recent campaigns" icon={<Radar />} />
          <Link to="/campaigns" className="operations-text-link">View all campaigns <ArrowUpRight size={16} /></Link>
        </div>
        <RecentCampaigns campaigns={campaigns} loading={campaignsLoading} error={campaignsError} />
      </Card>

      <Card className="dashboard-export-card">
        <div>
          <span className="dashboard-section-kicker"><FileBarChart size={15} /> Reporting</span>
          <h2>Share the complete awareness picture</h2>
          <p>Export organization-wide analytics for leadership review and program reporting.</p>
        </div>
        <div className="dashboard-export-controls" aria-label="Export analytics">
          <label>
            <span className="sr-only">Export format</span>
            <select aria-label="Export format" value={exportFormat} onChange={event => setExportFormat(event.target.value as ExportFormat)}>
              <option value="csv">CSV report</option>
              <option value="pdf">PDF report</option>
              <option value="xlsx">Excel report</option>
            </select>
          </label>
          <button type="button" className="btn-secondary" disabled={exporting} onClick={() => void handleExport()}>
            <Download size={16} /> {exporting ? 'Preparing…' : 'Export'}
          </button>
        </div>
        {exportError && <p role="alert" className="dashboard-export-error">{exportError}</p>}
      </Card>
    </div>
  );
}

function DashboardMetric({ title, value, detail, icon, tone }: {
  title: string;
  value: string | number;
  detail: string;
  icon: ReactNode;
  tone: string;
}) {
  return (
    <Card className={`dashboard-kpi dashboard-kpi-${tone}`}>
      <div className="dashboard-kpi-icon">{icon}</div>
      <div><p>{title}</p><strong>{value}</strong><span>{detail}</span></div>
    </Card>
  );
}

function RiskGauge({ score, loading }: { score: number | null; loading: boolean }) {
  const safeScore = score == null ? 0 : Math.min(100, Math.max(0, score));
  return (
    <div className="dashboard-risk-visual">
      <div
        className="dashboard-risk-ring"
        style={{ '--risk-progress': `${safeScore * 3.6}deg`, '--risk-color': getRiskColor(safeScore) } as CSSProperties}
        role="img"
        aria-label={loading ? 'Behavior indicator loading' : score === null ? 'Behavior indicators unavailable' : `Behavior indicator ${score} out of 100`}
      >
        <div><strong>{loading ? '…' : score ?? '—'}</strong><span>out of 100</span></div>
      </div>
      <small>{score === null ? 'No baseline' : getRiskBand(score)}</small>
    </div>
  );
}

function SectionHeading({ eyebrow, title, icon }: { eyebrow: string; title: string; icon: ReactNode }) {
  return (
    <div className="dashboard-section-heading">
      <span>{icon}</span>
      <div><p>{eyebrow}</p><h2>{title}</h2></div>
    </div>
  );
}

function TrendPanel({ data, loading, error }: { data: TrendData[]; loading: boolean; error: boolean }) {
  if (loading) return <PanelState>Loading behavior trends…</PanelState>;
  if (error) return <PanelState error>Behavior trends could not be loaded.</PanelState>;
  if (data.length === 0) return <PanelState>No trend data yet. Completed campaign activity will appear here.</PanelState>;
  const latest = data[data.length - 1];
  return (
    <>
      <p className="sr-only">
        Latest recorded rates: {percent(latest.open_rate)} opened, {percent(latest.click_rate)} clicked, and {percent(latest.submit_rate)} submitted.
      </p>
      <div className="dashboard-chart" role="img" aria-label="Open, click, and submit rate trend chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 5" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} stroke="var(--muted)" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis domain={[0, 100]} tickFormatter={value => `${value}%`} stroke="var(--muted)" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)' }}
              formatter={(value) => `${Number(value).toFixed(1)}%`}
            />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
            <Line type="monotone" dataKey="open_rate" name="Open rate" stroke="#2563eb" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="click_rate" name="Click rate" stroke="#d97706" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="submit_rate" name="Submit rate" stroke="#dc2626" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function FunnelPanel({ overview, loading }: { overview?: AnalyticsOverview; loading: boolean }) {
  if (loading) return <PanelState>Loading funnel…</PanelState>;
  if (!overview || !Number.isFinite(overview.emails_sent) || overview.emails_sent <= 0) {
    return <PanelState>No delivered simulations are available for the funnel.</PanelState>;
  }
  const sent = overview.emails_sent;
  const stages = [
    { label: 'Delivered', count: sent, rate: 100, tone: 'neutral' },
    { label: 'Opened', count: countFromRate(sent, overview.open_rate), rate: safeRate(overview.open_rate), tone: 'info' },
    { label: 'Clicked', count: countFromRate(sent, overview.click_rate), rate: safeRate(overview.click_rate), tone: 'warning' },
    { label: 'Submitted', count: countFromRate(sent, overview.submit_rate), rate: safeRate(overview.submit_rate), tone: 'danger' },
  ];
  return (
    <div className="dashboard-funnel">
      {stages.map(stage => (
        <div key={stage.label} className={`dashboard-funnel-stage funnel-${stage.tone}`}>
          <div><span>{stage.label}</span><strong>{stage.count}</strong><small>{stage.rate.toFixed(1)}%</small></div>
          <div className="dashboard-funnel-track"><span style={{ width: `${Math.max(3, Math.min(100, stage.rate))}%` }} /></div>
        </div>
      ))}
      <div className="dashboard-report-callout">
        <ShieldCheck size={18} />
        <div><strong>{countFromRate(sent, overview.report_rate)} reported</strong><span>{percent(overview.report_rate)} positive reporting behavior</span></div>
      </div>
    </div>
  );
}

function DepartmentPanel({ departments, loading, error }: {
  departments: RankedDepartment[];
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <PanelState>Loading department analytics…</PanelState>;
  if (error) return <PanelState error>Department analytics could not be loaded.</PanelState>;
  if (departments.length === 0) return <PanelState>Add recipient positions to compare audience segments.</PanelState>;
  return (
    <ol className="dashboard-department-list">
      {departments.slice(0, 5).map((department, index) => (
        <li key={department.department}>
          <span className="dashboard-rank">{index + 1}</span>
          <div className="dashboard-department-name"><strong>{department.department}</strong><span>{department.users_count} recipients</span></div>
          <div className="dashboard-department-rates">
            <span>{department.click_rate.toFixed(1)}% click</span>
            <span>{department.submit_rate.toFixed(1)}% submit</span>
          </div>
          <div className="dashboard-department-bar"><span style={{ width: `${department.exposure}%` }} /></div>
        </li>
      ))}
    </ol>
  );
}

function PriorityActions({ overview, risk }: { overview?: AnalyticsOverview; risk?: RiskScoreBreakdown }) {
  return (
    <div className="dashboard-action-list">
      {buildPriorityActions(overview, risk).map(action => (
        <Link key={action.title} to={action.to}>
          <span className={`dashboard-action-icon action-${action.tone}`}>{action.icon}</span>
          <span><strong>{action.title}</strong><small>{action.detail}</small></span>
          <ArrowUpRight size={16} />
        </Link>
      ))}
    </div>
  );
}

function RecentCampaigns({ campaigns, loading, error }: {
  campaigns?: CampaignSummaryRow[];
  loading: boolean;
  error: boolean;
}) {
  if (loading) return <PanelState>Loading recent campaigns…</PanelState>;
  if (error) return <PanelState error>Could not load recent campaigns.</PanelState>;
  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="operations-empty">
        <Radar size={30} /><p>No campaigns yet</p>
        <Link to="/campaigns/new" className="operations-text-link">Create your first simulation <ArrowUpRight size={16} /></Link>
      </div>
    );
  }
  return (
    <div className="dashboard-campaign-table" role="table" aria-label="Recent campaigns">
      <div className="dashboard-campaign-table-head" role="row">
        <span>Campaign</span><span>Audience</span><span>Outcomes</span><span>Launch</span><span className="sr-only">Details</span>
      </div>
      {campaigns.slice(0, 5).map(campaign => {
        const stats = campaign.stats;
        const total = stats?.total ?? 0;
        return (
          <Link key={campaign.id} to={`/campaigns/${campaign.id}`} className="dashboard-campaign-row">
            <span className="dashboard-campaign-primary" role="cell">
              <strong>{campaign.name}</strong>
              <small><span className={`badge ${campaignStatusBadge(campaign.status)}`}>{campaign.status}</span></small>
            </span>
            <span role="cell"><strong>{total}</strong><small>recipients</small></span>
            <span className="dashboard-campaign-outcomes" role="cell">
              <small><b>{rate(stats?.clicked, total)}</b> clicked</small>
              <small><b>{rate(stats?.submitted_data, total)}</b> submitted</small>
              <small><b>{rate(stats?.email_reported, total)}</b> reported</small>
            </span>
            <span role="cell"><strong>{formatDate(campaign.launch_date)}</strong><small>{formatTime(campaign.launch_date)}</small></span>
            <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        );
      })}
    </div>
  );
}

function PanelState({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <p className={`dashboard-panel-state ${error ? 'is-error' : ''}`} role={error ? 'alert' : 'status'}>{children}</p>;
}

interface RankedDepartment extends DepartmentStats {
  exposure: number;
}

function rankDepartments(departments: DepartmentStats[]): RankedDepartment[] {
  return departments
    .map(department => ({
      ...department,
      exposure: Math.min(100, department.click_rate * 0.55 + department.submit_rate * 0.45),
    }))
    .sort((left, right) => right.exposure - left.exposure || right.users_count - left.users_count);
}

function buildPriorityActions(overview?: AnalyticsOverview, risk?: RiskScoreBreakdown) {
  if (!overview || overview.emails_sent === 0) {
    return [
      { title: 'Launch a baseline campaign', detail: 'Establish your first measurable awareness benchmark.', to: '/campaigns/new', tone: 'info', icon: <Send size={18} /> },
      { title: 'Prepare audience groups', detail: 'Organize employees before launching simulations.', to: '/groups', tone: 'neutral', icon: <Users size={18} /> },
      { title: 'Review reporting workflow', detail: 'Make sure reported messages reach the review queue.', to: '/reported-messages', tone: 'success', icon: <ShieldCheck size={18} /> },
    ];
  }
  const actions = [];
  if (overview.submit_rate >= 5) actions.push({ title: 'Prioritize credential handling', detail: `${percent(overview.submit_rate)} of recipients submitted data.`, to: '/campaigns', tone: 'danger', icon: <AlertTriangle size={18} /> });
  if (overview.click_rate >= 10) actions.push({ title: 'Coach repeat click behavior', detail: `${percent(overview.click_rate)} clicked a simulation link.`, to: '/analytics/at-risk', tone: 'warning', icon: <MousePointer size={18} /> });
  if (overview.report_rate < 20) actions.push({ title: 'Strengthen reporting habits', detail: `Reporting is ${percent(overview.report_rate)} across delivered simulations.`, to: '/reported-messages', tone: 'success', icon: <ShieldCheck size={18} /> });
  actions.push({ title: 'Review full analytics', detail: risk?.recommendations?.[0] ?? 'Compare campaigns and monitor changes over time.', to: '/analytics/reports', tone: 'info', icon: <BarChart3 size={18} /> });
  return actions.slice(0, 4);
}

function metricValue(loading: boolean, error: boolean, value: string | number | undefined): string | number {
  if (loading) return '…';
  if (error || value == null || value === '') return 'Unavailable';
  return value;
}

function derivedCount(
  overview: AnalyticsOverview | undefined,
  key: 'open_rate' | 'click_rate' | 'submit_rate' | 'report_rate',
  loading: boolean,
  error: boolean,
): string | number {
  if (loading) return '…';
  if (error || !overview) return 'Unavailable';
  return countFromRate(overview.emails_sent, overview[key]);
}

function countFromRate(total: number, value: number): number {
  return Math.round(total * safeRate(value) / 100);
}

function safeRate(value: number | undefined): number {
  return Number.isFinite(value) ? value! : 0;
}

function rate(value: number | undefined, total: number): string {
  if (value == null || total <= 0) return '—';
  return `${Math.round(value / total * 100)}%`;
}

function shortDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date.getFullYear() < 2000 ? 'Not scheduled' : date.toLocaleDateString();
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date.getFullYear() < 2000 ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function campaignStatusBadge(status: string): string {
  if (status === 'In progress') return 'badge-yellow';
  if (status === 'Completed' || status === 'Emails Sent') return 'badge-green';
  return 'badge-gray';
}

function defaultRecommendations(overview?: AnalyticsOverview): string[] {
  if (!overview || overview.emails_sent === 0) return ['Launch a baseline simulation to establish measurable behavior indicators.'];
  const recommendations = [];
  if (overview.submit_rate > 0) recommendations.push('Focus coaching on credential and form-submission decisions.');
  if (overview.click_rate >= 10) recommendations.push('Reinforce link verification before employees take action.');
  if (overview.report_rate < 20) recommendations.push('Promote the reporting workflow and reinforce positive reporting behavior.');
  return recommendations.length > 0 ? recommendations : ['Continue simulations and monitor behavior changes over time.'];
}

function getRiskColor(score: number): string {
  if (score >= 70) return '#dc2626';
  if (score >= 40) return '#d97706';
  return '#15803d';
}

function getRiskBand(score: number): string {
  if (score >= 75) return 'Critical';
  if (score >= 50) return 'High';
  if (score >= 25) return 'Medium';
  return 'Low';
}

function getRiskLabel(score: number): string {
  if (score >= 70) return 'More clicks and submissions, with fewer positive reports, were observed.';
  if (score >= 40) return 'Mixed simulation outcomes indicate targeted reinforcement opportunities.';
  return 'More positive reporting and fewer high-risk simulation actions were observed.';
}
